import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Languages } from 'lucide-react'

interface LanguageSwitcherProps {
  variant?: 'floating' | 'inline' | 'menu'
  className?: string
}

export default function LanguageSwitcher({ variant = 'floating', className }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const changeLang = (lng: 'tr' | 'en') => {
    i18n.changeLanguage(lng)
    try {
      localStorage.setItem('i18nextLng', lng)
    } catch {}
  }

  const current = (i18n.language || 'tr').startsWith('en') ? 'en' : 'tr'
  const wrapper = variant === 'floating' ? 'fixed bottom-4 right-4 z-50 safe-area-bottom safe-area-right' : (variant === 'menu' ? 'relative' : '')

  useEffect(() => {
    if (variant !== 'menu') return
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current) return
      if (!menuRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [variant])

  return (
    <div className={`${wrapper}${className ? ` ${className}` : ''}`} ref={menuRef}>
      {variant === 'menu' ? (
        <>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center space-x-2 transition-colors"
            aria-label={t('common.changeLanguage')}
          >
            <Languages className="w-4 h-4" />
            <span className="text-xs hidden sm:inline">{current.toUpperCase()}</span>
          </button>
          {isOpen && (
            <div className="absolute right-0 mt-2 w-28 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl shadow-xl p-1 z-50">
              <button
                onClick={() => { changeLang('tr'); setIsOpen(false) }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm ${current === 'tr' ? 'bg-violet-600 text-white' : 'text-white/80 hover:bg-white/10'}`}
              >
                TR
              </button>
              <button
                onClick={() => { changeLang('en'); setIsOpen(false) }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm ${current === 'en' ? 'bg-violet-600 text-white' : 'text-white/80 hover:bg-white/10'}`}
              >
                EN
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white/10 hover:bg-white/20 transition-colors backdrop-blur-sm border border-white/20 rounded-xl p-1 flex items-center">
          <button
            onClick={() => changeLang('tr')}
            className={`px-3 py-1 rounded-lg text-xs sm:text-sm font-medium ${current === 'tr' ? 'bg-violet-600 text-white' : 'text-white/80 hover:bg-white/10'}`}
            aria-label={t('common.turkish')}
          >
            TR
          </button>
          <button
            onClick={() => changeLang('en')}
            className={`px-3 py-1 rounded-lg text-xs sm:text-sm font-medium ${current === 'en' ? 'bg-violet-600 text-white' : 'text-white/80 hover:bg-white/10'}`}
            aria-label={t('common.english')}
          >
            EN
          </button>
        </div>
      )}
    </div>
  )
}



